<template>
  <div class="dash-root">
    <!-- ═══ Header ════════════════════════════════════════ -->
    <div class="dash-header">
      <div class="dash-header__inner">
        <div class="dash-header__brand">
          <span class="dash-header__icon">
            <i class="fas fa-handshake"></i>
          </span>
          <div>
            <h1 class="dash-header__title">Partners</h1>
            <p class="dash-header__subtitle">Manage, communicate & broadcast</p>
          </div>
        </div>
        <div class="dash-header__actions">
          <transition name="pop">
            <span v-if="selectedPartners.length > 0" class="selection-pill">
              <i class="fas fa-check-circle"></i>
              <span class="pill-text">{{ selectedPartners.length }} selected</span>
            </span>
          </transition>
          <button class="btn btn--templates-manager" @click="openTemplateManager">
            <i class="fas fa-layer-group"></i>
            <span class="btn-label">Templates</span>
          </button>
          <button
            class="btn btn--broadcast"
            :class="{ 'btn--broadcast--active': selectedPartners.length > 0 }"
            @click="openBroadcastDialog"
            :disabled="selectedPartners.length === 0"
          >
            <i class="fas fa-bullhorn"></i>
            <span class="btn-label">Broadcast</span>
          </button>
          <button class="btn btn--refresh" @click="fetchPartners" :disabled="loading" title="Refresh">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card" v-for="stat in statsCards" :key="stat.key">
          <div class="stat-card__icon" :style="{ background: stat.bg, color: stat.color }">
            <i :class="stat.icon"></i>
          </div>
          <div class="stat-card__body">
            <span class="stat-card__val">{{ stat.value }}</span>
            <span class="stat-card__lbl">{{ stat.label }}</span>
          </div>
          <div class="stat-card__bar" :style="{ background: stat.color, width: stat.percent + '%' }"></div>
        </div>
      </div>
    </div>

    <!-- ═══ Table ═════════════════════════════════════════ -->
    <div class="table-wrap">
      <div class="table-toolbar">
        <div class="toolbar-top-row">
          <div class="filter-group">
            <i class="fas fa-filter filter-group__icon"></i>
            <v-select
              v-model="typeFilter"
              :items="typeOptions"
              label="Filter by type"
              outlined
              dense
              clearable
              hide-details
              class="filter-select"
            ></v-select>
          </div>
          <button
            class="btn btn--select-all"
            @click="toggleSelectAll"
            :title="allFilteredSelected ? 'Deselect all' : 'Select all'"
          >
            <i :class="allFilteredSelected ? 'fas fa-check-square' : 'far fa-square'"></i>
            <span class="btn-label">{{ allFilteredSelected ? 'Deselect all' : 'Select all' }}</span>
          </button>
          <span class="table-meta">
            <i class="fas fa-users"></i>
            {{ filteredPartners.length }} partner{{ filteredPartners.length === 1 ? '' : 's' }}
          </span>
        </div>
        <div class="search-group">
          <i class="fas fa-search search-group__icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search by name..."
          />
          <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Desktop table -->
      <div class="desktop-table">
        <v-data-table
          v-model="selectedPartners"
          :headers="headers"
          :items="filteredPartners"
          :loading="loading"
          class="partners-table elevation-0"
          item-key="id"
          show-select
          :items-per-page="10"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
        >
          <template v-slot:item.type="{ item }">
            <span class="type-badge" :class="'type-badge--' + item.type.toLowerCase()">
              <i :class="getTypeIcon(item.type)"></i>
              {{ item.type }}
            </span>
          </template>
          <template v-slot:item.phone_number="{ item }">
            <span class="phone-cell">
              <i class="fab fa-whatsapp" v-if="item.phone_number"></i>
              <span v-else class="phone-cell--missing"><i class="fas fa-minus"></i></span>
              {{ item.phone_number || '—' }}
            </span>
          </template>
          <template v-slot:item.total_affiliates="{ item }">
            <span class="affiliate-count">
              <i class="fas fa-user-friends"></i>
              {{ item.total_affiliates || 0 }}
            </span>
          </template>
          <template v-slot:item.created_at="{ item }">
            <span class="date-cell">{{ formatDate(item.created_at) }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="action-group">
              <button class="action-btn action-btn--view" title="View details" @click.stop="viewDetails(item.id)">
                <i class="fas fa-eye"></i>
              </button>
              <button class="action-btn action-btn--email" title="Send email" @click="openCommunication(item, 'email')">
                <i class="fas fa-envelope"></i>
              </button>
              <button class="action-btn action-btn--whatsapp" title="Send WhatsApp" @click="openCommunication(item, 'whatsapp')">
                <i class="fab fa-whatsapp"></i>
              </button>
              <button class="action-btn action-btn--push" title="Send WebPush" @click="openCommunication(item, 'webpush')">
                <i class="fas fa-bell"></i>
              </button>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="empty-table">
              <i class="fas fa-handshake"></i>
              <p>No partners found</p>
              <small>Try adjusting your filters or add new partners</small>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- Mobile card list -->
      <div class="mobile-cards">
        <div v-if="loading" class="mobile-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading…
        </div>
        <div v-else-if="filteredPartners.length === 0" class="empty-table">
          <i class="fas fa-handshake"></i>
          <p>No partners found</p>
          <small>Try adjusting your filters</small>
        </div>
        <div
          v-else
          v-for="item in filteredPartners"
          :key="item.id"
          class="partner-card"
          :class="{ 'partner-card--selected': isSelected(item) }"
        >
          <div class="partner-card__top">
            <label class="partner-card__check">
              <input
                type="checkbox"
                :checked="isSelected(item)"
                @change="toggleSelect(item)"
              />
            </label>
            <div class="partner-card__info">
              <div class="partner-card__name">{{ item.username }}</div>
              <div class="partner-card__email">{{ item.email || '—' }}</div>
            </div>
            <span class="type-badge" :class="'type-badge--' + item.type.toLowerCase()">
              <i :class="getTypeIcon(item.type)"></i>
              {{ item.type }}
            </span>
          </div>
          <div class="partner-card__meta">
            <span class="phone-cell" v-if="item.phone_number">
              <i class="fab fa-whatsapp"></i> {{ item.phone_number }}
            </span>
            <span class="affiliate-count">
              <i class="fas fa-user-friends"></i> {{ item.total_affiliates || 0 }}
            </span>
            <span class="date-cell">{{ formatDate(item.created_at) }}</span>
          </div>
          <div class="partner-card__actions">
            <button class="action-btn action-btn--view" @click.stop="viewDetails(item.id)">
              <i class="fas fa-eye"></i> View
            </button>
            <button class="action-btn action-btn--email" @click="openCommunication(item, 'email')">
              <i class="fas fa-envelope"></i>
            </button>
            <button class="action-btn action-btn--whatsapp" @click="openCommunication(item, 'whatsapp')">
              <i class="fab fa-whatsapp"></i>
            </button>
            <button class="action-btn action-btn--push" @click="openCommunication(item, 'webpush')">
              <i class="fas fa-bell"></i>
            </button>
          </div>
        </div>
        <div v-if="filteredPartners.length > 0" class="mobile-count">
          {{ filteredPartners.length }} partner{{ filteredPartners.length === 1 ? '' : 's' }}
        </div>
      </div>
    </div>

    <!-- ═══ Individual Communication Modal ════════════════ -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-card modal-card--comm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon" :class="'modal-card__icon--' + selectedChannel">
                <i :class="channelIcon(selectedChannel)"></i>
              </span>
              <div>
                <div class="modal-card__title">Send {{ channelLabel(selectedChannel) }}</div>
                <div class="modal-card__subtitle">To {{ selectedPartner ? selectedPartner.username : '' }}</div>
              </div>
            </div>
            <button class="modal-close" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-card__body modal-body--split">
            <!-- LEFT: Form -->
            <div class="modal-form-col">
              <div class="channel-picker">
                <button
                  v-for="ch in channels"
                  :key="ch.value"
                  class="channel-chip"
                  :class="{ 'channel-chip--active': selectedChannel === ch.value }"
                  @click="selectedChannel = ch.value"
                >
                  <i :class="ch.icon"></i>
                  <span>{{ ch.text }}</span>
                </button>
              </div>

              <button class="btn-use-template" @click="openTemplatePickerDialog('individual')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <transition name="field-slide">
                <div v-if="selectedChannel === 'email'" class="form-group">
                  <label class="form-label">Subject</label>
                  <input v-model="emailSubject" type="text" class="form-input" placeholder="Enter subject..." />
                </div>
              </transition>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → partner name · {link} → chat link</span>
                </label>
                <textarea
                  v-model="messageText"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your message…"
                  @input="updatePreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': messageText.length > 900 }">
                    {{ messageText.length }}/1000
                  </div>
                  <button v-if="messageText.trim()" class="btn-save-tpl" @click="openSaveTemplate('individual')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <!-- RIGHT: Preview -->
            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Live Preview</span>
                  <span class="preview-channel">{{ channelLabel(selectedChannel) }}</span>
                </div>
                <div v-if="selectedChannel === 'whatsapp'" class="preview-whatsapp">
                  <div class="wa-phone">
                    <div class="wa-phone__header"><i class="fab fa-whatsapp"></i><span>WhatsApp</span></div>
                    <div class="wa-phone__chat">
                      <div class="wa-bubble">
                        <div class="wa-bubble__text">{{ previewMessage }}</div>
                        <div class="wa-bubble__meta">
                          <span class="wa-bubble__time">{{ currentTime }}</span>
                          <span class="wa-bubble__tick">✓✓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="selectedChannel === 'email'" class="preview-email">
                  <div class="email-frame">
                    <div class="email-frame__bar">
                      <div class="email-frame__dots"><span></span><span></span><span></span></div>
                      <div class="email-frame__url">pettycashglobal.com</div>
                    </div>
                    <div class="email-frame__body">
                      <div class="email-header">
                        <div class="email-header__from">
                          <div class="email-avatar">PC</div>
                          <div>
                            <div class="email-header__name">PettyCash Manager</div>
                            <div class="email-header__addr">manager@pettycashglobal.com</div>
                          </div>
                        </div>
                        <div class="email-header__date">{{ currentDate }}</div>
                      </div>
                      <div class="email-subject">{{ emailSubject || '(no subject)' }}</div>
                      <div class="email-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="selectedChannel === 'webpush'" class="preview-webpush">
                  <div class="push-frame">
                    <div class="push-frame__header">
                      <div class="push-app">
                        <div class="push-app__icon"><i class="fas fa-handshake"></i></div>
                        <div class="push-app__name">PettyCash</div>
                      </div>
                      <div class="push-time">now</div>
                    </div>
                    <div class="push-content">
                      <div class="push-title">Message from Manager</div>
                      <div class="push-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeModal">Cancel</button>
            <button
              class="btn btn--send"
              :class="'btn--send--' + selectedChannel"
              :disabled="!messageText.trim() || sending"
              @click="sendCommunication"
            >
              <i v-if="sending" class="fas fa-spinner fa-spin"></i>
              <i v-else :class="channelIcon(selectedChannel)"></i>
              {{ sending ? 'Sending…' : 'Send ' + channelLabel(selectedChannel) }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Broadcast Modal ═══════════════════════════════ -->
    <transition name="modal">
      <div v-if="broadcastDialog" class="modal-overlay" @click.self="closeBroadcastDialog">
        <div class="modal-card modal-card--broadcast">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--broadcast">
                <i class="fas fa-bullhorn"></i>
              </span>
              <div>
                <div class="modal-card__title">Broadcast Message</div>
                <div class="modal-card__subtitle">
                  {{ selectedPartners.length }} recipient{{ selectedPartners.length === 1 ? '' : 's' }} selected
                </div>
              </div>
            </div>
            <button class="modal-close" @click="closeBroadcastDialog"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-card__body modal-body--split">
            <!-- LEFT: Form -->
            <div class="modal-form-col">
              <div class="recipient-bar">
                <span class="recipient-bar__label">To:</span>
                <div class="recipient-chips">
                  <span v-for="p in selectedPartners.slice(0, 5)" :key="p.id" class="recipient-chip">{{ p.username }}</span>
                  <span v-if="selectedPartners.length > 5" class="recipient-chip recipient-chip--more">+{{ selectedPartners.length - 5 }} more</span>
                </div>
              </div>

              <div class="channel-picker">
                <button
                  v-for="ch in broadcastChannels"
                  :key="ch.value"
                  class="channel-chip"
                  :class="{ 'channel-chip--active': broadcastChannel === ch.value }"
                  @click="broadcastChannel = ch.value"
                >
                  <i :class="ch.icon"></i>
                  <span>{{ ch.text }}</span>
                </button>
              </div>

              <button class="btn-use-template" @click="openTemplatePickerDialog('broadcast')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <transition name="field-slide">
                <div v-if="broadcastChannel === 'email'" class="form-group">
                  <label class="form-label">Subject</label>
                  <input v-model="broadcastSubject" type="text" class="form-input" placeholder="Enter subject..." />
                </div>
              </transition>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → partner name · {link} → chat link</span>
                </label>
                <textarea
                  v-model="broadcastMessage"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your broadcast message…"
                  @input="updateBroadcastPreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': broadcastMessage.length > 900 }">
                    {{ broadcastMessage.length }}/1000
                  </div>
                  <button v-if="broadcastMessage.trim()" class="btn-save-tpl" @click="openSaveTemplate('broadcast')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <!-- RIGHT: Preview -->
            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Preview (first recipient)</span>
                  <span class="preview-channel">{{ channelLabel(broadcastChannel) }}</span>
                </div>
                <div v-if="broadcastChannel === 'email'" class="preview-email">
                  <div class="email-frame">
                    <div class="email-frame__bar">
                      <div class="email-frame__dots"><span></span><span></span><span></span></div>
                      <div class="email-frame__url">pettycashglobal.com</div>
                    </div>
                    <div class="email-frame__body">
                      <div class="email-header">
                        <div class="email-header__from">
                          <div class="email-avatar">PC</div>
                          <div>
                            <div class="email-header__name">PettyCash Manager</div>
                            <div class="email-header__addr">admin@pettycashglobal.com</div>
                          </div>
                        </div>
                        <div class="email-header__date">{{ currentDate }}</div>
                      </div>
                      <div class="email-subject">{{ broadcastSubject || '(no subject)' }}</div>
                      <div class="email-body">{{ broadcastPreview }}</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="broadcastChannel === 'webpush'" class="preview-webpush">
                  <div class="push-frame">
                    <div class="push-frame__header">
                      <div class="push-app">
                        <div class="push-app__icon"><i class="fas fa-handshake"></i></div>
                        <div class="push-app__name">PettyCash</div>
                      </div>
                      <div class="push-time">now</div>
                    </div>
                    <div class="push-content">
                      <div class="push-title">Message from Manager</div>
                      <div class="push-body">{{ broadcastPreview }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeBroadcastDialog">Cancel</button>
            <button
              class="btn btn--send btn--send--broadcast"
              :disabled="!broadcastMessage.trim() || sendingBroadcast"
              @click="sendBroadcast"
            >
              <i v-if="sendingBroadcast" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
              {{ sendingBroadcast ? 'Sending…' : 'Broadcast to ' + selectedPartners.length }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Picker Dialog ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplatePickerDialog" class="modal-overlay" @click.self="closeTemplatePickerDialog">
        <div class="modal-card modal-card--picker">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-layer-group"></i>
              </span>
              <div>
                <div class="modal-card__title">Choose a Partner Template</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} available</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplatePickerDialog"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
              <small>Create your first template to reuse messages</small>
            </div>
            <div v-else>
              <div class="tpl-search tpl-search--dialog">
                <i class="fas fa-search"></i>
                <input
                  v-model="templatePickerSearch"
                  type="text"
                  placeholder="Search templates by name, subject or content..."
                />
                <button v-if="templatePickerSearch" class="search-clear" @click="templatePickerSearch = ''">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="tpl-picker-list">
                <div
                  v-for="tpl in filteredPickerTemplates"
                  :key="tpl.id"
                  class="tpl-picker-item"
                  @click="selectTemplateFromPicker(tpl)"
                >
                  <div class="tpl-picker-item__top">
                    <span class="tpl-item__name">{{ tpl.name }}</span>
                    <span class="tpl-all-channels-badge">
                      <i class="fas fa-share-alt"></i>
                      All channels
                    </span>
                  </div>
                  <p v-if="tpl.subject" class="tpl-picker-item__subject">
                    <i class="fas fa-heading"></i> {{ tpl.subject }}
                  </p>
                  <p class="tpl-picker-item__body">{{ tpl.message }}</p>
                  <div class="tpl-picker-item__footer">
                    <span class="tpl-manager-item__date">
                      <i class="fas fa-clock"></i>
                      {{ formatDate(tpl.created_at) }}
                    </span>
                    <div class="tpl-compat-icons">
                      <i class="fas fa-envelope" title="Email"></i>
                      <i class="fab fa-whatsapp" title="WhatsApp"></i>
                      <i class="fas fa-bell" title="WebPush"></i>
                    </div>
                  </div>
                </div>
                <div v-if="filteredPickerTemplates.length === 0" class="tpl-empty tpl-empty--big">
                  <i class="fas fa-search"></i>
                  <p>No matching templates</p>
                  <small>Try a different search term</small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplatePickerDialog">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Save Template Dialog ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showSaveTemplateDialog" class="modal-overlay" @click.self="closeSaveTemplate">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-bookmark"></i>
              </span>
              <div>
                <div class="modal-card__title">Save Template</div>
                <div class="modal-card__subtitle">Reuse this message later on any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeSaveTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input
                v-model="templateName"
                type="text"
                class="form-input"
                placeholder="e.g. Welcome new investor"
                ref="tplNameInput"
                @keyup.enter="confirmSaveTemplate"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Subject
                <span class="form-hint">Optional — auto-filled for email</span>
              </label>
              <input v-model="saveTemplateSubject" type="text" class="form-input" placeholder="Leave empty if not applicable…" />
            </div>
            <div class="form-group">
              <label class="form-label">Message</label>
              <div class="tpl-msg-preview">{{ saveTemplateMessage }}</div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeSaveTemplate">Cancel</button>
            <button
              class="btn btn--save-confirm"
              :disabled="!templateName.trim() || savingTemplate"
              @click="confirmSaveTemplate"
            >
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Save Template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Manager Modal ════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplateManager" class="modal-overlay" @click.self="closeTemplateManager">
        <div class="modal-card modal-card--manager">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-layer-group"></i>
              </span>
              <div>
                <div class="modal-card__title">Partner Message Templates</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} saved</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplateManager"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
            </div>
            <div v-else class="tpl-manager-list">
              <div v-for="tpl in templates" :key="tpl.id" class="tpl-manager-item">
                <div class="tpl-manager-item__top">
                  <span class="tpl-item__name">{{ tpl.name }}</span>
                  <div class="tpl-manager-item__actions">
                    <span class="tpl-all-channels-badge">
                      <i class="fas fa-share-alt"></i>
                      All channels
                    </span>
                    <button class="tpl-del-btn" @click="requestDeleteTemplate(tpl)" title="Delete">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <p v-if="tpl.subject" class="tpl-manager-item__subject">
                  <i class="fas fa-heading"></i> {{ tpl.subject }}
                </p>
                <p class="tpl-manager-item__body">{{ tpl.message }}</p>
                <div class="tpl-manager-item__footer">
                  <span class="tpl-manager-item__date">
                    <i class="fas fa-clock"></i>
                    {{ formatDate(tpl.created_at) }}
                  </span>
                  <div class="tpl-compat-icons">
                    <i class="fas fa-envelope" title="Email"></i>
                    <i class="fab fa-whatsapp" title="WhatsApp"></i>
                    <i class="fas fa-bell" title="WebPush"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplateManager">Close</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Create Template Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showCreateTemplateDialog" class="modal-overlay" @click.self="closeCreateTemplateDialog">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-plus-circle"></i>
              </span>
              <div>
                <div class="modal-card__title">Create new template</div>
                <div class="modal-card__subtitle">Save a reusable message for any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeCreateTemplateDialog">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input
                v-model="createTemplateName"
                type="text"
                class="form-input"
                placeholder="e.g. Welcome new investor"
                ref="createTplNameInput"
                @keyup.enter="confirmCreateTemplate"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Subject
                <span class="form-hint">Optional — mainly for email</span>
              </label>
              <input
                v-model="createTemplateSubject"
                type="text"
                class="form-input"
                placeholder="Leave empty if not applicable…"
              />
            </div>
            <div class="form-group">
              <label class="form-label">
                Message
                <span class="form-hint">{name} → partner name · {link} → chat link</span>
              </label>
              <textarea
                v-model="createTemplateMessage"
                class="form-textarea"
                rows="5"
                placeholder="Type your template message…"
              ></textarea>
              <div class="form-actions-row">
                <div class="char-count" :class="{ 'char-count--warn': createTemplateMessage.length > 900 }">
                  {{ createTemplateMessage.length }}/1000
                </div>
              </div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeCreateTemplateDialog">Cancel</button>
            <button
              class="btn btn--save-confirm"
              :disabled="!createTemplateName.trim() || savingTemplate"
              @click="confirmCreateTemplate"
            >
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Create template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Delete Confirmation Mini Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showDeleteConfirmation" class="modal-overlay" @click.self="cancelDeleteTemplate">
        <div class="modal-card modal-card--confirm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--delete">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <div>
                <div class="modal-card__title">Delete Template</div>
                <div class="modal-card__subtitle">This action cannot be undone</div>
              </div>
            </div>
            <button class="modal-close" @click="cancelDeleteTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <p class="confirm-text">
              Are you sure you want to delete the template<br>
             <strong>"{{ templateToDelete ? templateToDelete.name : '' }}"</strong>
            </p>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="cancelDeleteTemplate">Cancel</button>
            <button class="btn btn--delete-confirm" @click="confirmDeleteTemplate">
              <i class="fas fa-trash-alt"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

const CHAT_LINK = 'https://myapp.pettycashglobal.com/?open_chat=true';

export default {
  name: 'PartnersList',

  data() {
    return {
      partners: [],
      loading: false,
      typeFilter: null,
      typeOptions: ['Marketer', 'Investor'],
      selectedPartners: [],
      searchQuery: '',
      headers: [
        { text: 'Username',  value: 'username',         width: '160px' },
        { text: 'Type',      value: 'type',             width: '120px' },
        { text: 'Email',     value: 'email',            width: '200px' },
        { text: 'Phone',     value: 'phone_number',     width: '140px' },
        { text: 'Referrals', value: 'total_affiliates', width: '90px', align: 'center' },
        { text: 'Joined',    value: 'created_at',       width: '110px' },
        { text: 'Actions',   value: 'actions',          sortable: false, width: '150px', align: 'center' },
      ],

      channels: [
        { text: 'Email',    value: 'email',    icon: 'fas fa-envelope' },
        { text: 'WhatsApp', value: 'whatsapp', icon: 'fab fa-whatsapp' },
        { text: 'WebPush',  value: 'webpush',  icon: 'fas fa-bell' },
      ],
      broadcastChannels: [
        { text: 'Email',   value: 'email',   icon: 'fas fa-envelope' },
        { text: 'WebPush', value: 'webpush', icon: 'fas fa-bell' },
      ],

      showModal: false,
      selectedChannel: 'email',
      selectedPartner: null,
      emailSubject: 'Message from PettyCash',
      messageText: '',
      sending: false,
      previewMessage: '',

      broadcastDialog: false,
      broadcastChannel: 'email',
      broadcastSubject: 'Message from PettyCash',
      broadcastMessage: '',
      sendingBroadcast: false,
      broadcastPreview: '',

      showTemplatePickerDialog: false,
      templatePickerSource: 'individual',
      templatePickerSearch: '',

      templates: [],
      templatesLoading: false,

      showSaveTemplateDialog: false,
      templateName: '',
      saveTemplateSubject: '',
      saveTemplateMessage: '',
      savingTemplate: false,
      saveTemplateSource: 'individual',

      showTemplateManager: false,

      showCreateTemplateDialog: false,
      createTemplateName: '',
      createTemplateSubject: '',
      createTemplateMessage: '',

      // Suppression
      showDeleteConfirmation: false,
      templateToDelete: null,
    };
  },

  computed: {
    filteredPartners() {
      let list = this.partners;
      if (this.typeFilter) list = list.filter(p => p.type === this.typeFilter);
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(p => p.username.toLowerCase().includes(q));
      }
      return list;
    },

    filteredPickerTemplates() {
      if (!this.templatePickerSearch.trim()) return this.templates;
      const q = this.templatePickerSearch.toLowerCase().trim();
      return this.templates.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.message && t.message.toLowerCase().includes(q)) ||
        (t.subject && t.subject.toLowerCase().includes(q))
      );
    },

    statsCards() {
      const total = this.partners.length || 1;
      return [
        { key: 'total',    label: 'Total Partners', value: this.partners.length,         icon: 'fas fa-users',      color: '#5c6bc0', bg: '#eef0fc', percent: 100 },
        { key: 'marketer', label: 'Marketers',      value: this.countByType('Marketer'), icon: 'fas fa-bullhorn',   color: '#2e7d32', bg: '#e8f5e9', percent: (this.countByType('Marketer') / total) * 100 },
        { key: 'investor', label: 'Investors',      value: this.countByType('Investor'), icon: 'fas fa-chart-line', color: '#6a1b9a', bg: '#f3e5f5', percent: (this.countByType('Investor') / total) * 100 },
      ];
    },

    currentTime() {
      return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    },
    currentDate() {
      return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    allFilteredSelected() {
      if (this.filteredPartners.length === 0) return false;
      return this.selectedPartners.length === this.filteredPartners.length &&
             this.filteredPartners.every(fp => this.selectedPartners.some(sp => sp.id === fp.id));
    },
  },

  mounted() {
    this.fetchPartners();
    this.fetchTemplates();
    document.addEventListener('keydown', this.handleEscape);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscape);
  },

  methods: {
    handleEscape(e) {
      if (e.key !== 'Escape') return;
      // Fermer d'abord les sous-modales
      if (this.showDeleteConfirmation) { this.cancelDeleteTemplate(); return; }
      if (this.showTemplatePickerDialog) { this.closeTemplatePickerDialog(); return; }
      if (this.showSaveTemplateDialog) { this.closeSaveTemplate(); return; }
      if (this.showTemplateManager)    { this.closeTemplateManager(); return; }
      if (this.showCreateTemplateDialog) { this.closeCreateTemplateDialog(); return; }
      if (this.showModal)              { this.closeModal(); return; }
      if (this.broadcastDialog)        { this.closeBroadcastDialog(); return; }
    },

    toggleSelectAll() {
      if (this.allFilteredSelected) {
        this.selectedPartners = [];
      } else {
        this.selectedPartners = this.filteredPartners.slice();
      }
    },

    async fetchPartners() {
      this.loading = true;
      try {
        const response = await axios.get('/api/v1/manager/partners/list/');
        this.partners = response.data.filter(p => p.type === 'Marketer' || p.type === 'Investor');
      } catch (error) {
        this.showSnackbar('Error loading partners', 'red darken-4');
      } finally {
        this.loading = false;
      }
    },

    isSelected(item) {
      return this.selectedPartners.some(p => p.id === item.id);
    },

    toggleSelect(item) {
      const idx = this.selectedPartners.findIndex(p => p.id === item.id);
      if (idx > -1) {
        this.selectedPartners.splice(idx, 1);
      } else {
        this.selectedPartners.push(item);
      }
    },

    countByType(type) { return this.partners.filter(p => p.type === type).length; },
    getTypeIcon(type) { return { Marketer: 'fas fa-bullhorn', Investor: 'fas fa-chart-line' }[type] || 'fas fa-user'; },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    channelIcon(channel) {
      return { email: 'fas fa-envelope', whatsapp: 'fab fa-whatsapp', webpush: 'fas fa-bell' }[channel] || 'fas fa-paper-plane';
    },
    channelLabel(channel) {
      return { email: 'Email', whatsapp: 'WhatsApp', webpush: 'WebPush' }[channel] || channel;
    },
    viewDetails(id) { this.$router.push('/savings/partners/detail/' + id).catch(() => {}); },

    openCommunication(partner, channel) {
      this.selectedPartner = partner;
      this.selectedChannel = channel;
      this.messageText = '';
      this.emailSubject = 'Message from PettyCash';
      this.previewMessage = this.renderPreview('', partner);
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.messageText = '';
      this.selectedPartner = null;
    },
    updatePreview() { this.previewMessage = this.renderPreview(this.messageText, this.selectedPartner); },
    renderPreview(text, partner) {
      if (!text) return 'Your message will appear here…';
      return text
        .replace(/{name}/g, partner?.username || 'John Doe')
        .replace(/{link}/g, CHAT_LINK);
    },

    async sendCommunication() {
      if (!this.messageText.trim()) { this.showSnackbar('Please enter a message', 'red darken-4'); return; }
      this.sending = true;
      try {
        const id = this.selectedPartner.id;
        let endpoint, payload;
        if (this.selectedChannel === 'email') {
          endpoint = `/api/v1/manager/partners/${id}/send-email/`;
          payload = { subject: this.emailSubject, message: this.messageText };
        } else if (this.selectedChannel === 'whatsapp') {
          endpoint = `/api/v1/manager/partners/${id}/send-whatsapp/`;
          payload = { message: this.messageText };
        } else {
          endpoint = `/api/v1/manager/partners/${id}/send-webpush/`;
          payload = { title: 'Message from Manager', message: this.messageText };
        }
        const response = await axios.post(endpoint, payload);
        if (this.selectedChannel === 'whatsapp') {
          if (response.data.whatsapp_url) {
            window.open(response.data.whatsapp_url, '_blank');
            this.closeModal();
          } else {
            this.showSnackbar(response.data.message || 'WhatsApp link unavailable', 'error');
          }
        } else {
          this.showSnackbar(response.data.message || 'Message sent', 'success');
          this.closeModal();
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error sending message', 'red darken-4');
      } finally {
        this.sending = false;
      }
    },

    openBroadcastDialog() {
      if (!this.selectedPartners.length) return;
      this.broadcastMessage = '';
      this.broadcastSubject = 'Message from PettyCash';
      this.broadcastChannel = 'email';
      this.broadcastPreview = this.renderPreview('', this.selectedPartners[0]);
      this.broadcastDialog = true;
    },
    closeBroadcastDialog() {
      this.broadcastDialog = false;
      this.broadcastMessage = '';
    },
    updateBroadcastPreview() {
      this.broadcastPreview = this.renderPreview(this.broadcastMessage, this.selectedPartners[0]);
    },

    async sendBroadcast() {
      if (!this.broadcastMessage.trim()) { this.showSnackbar('Please enter a message', 'red darken-4'); return; }
      this.sendingBroadcast = true;
      try {
        const payload = {
          partner_ids: this.selectedPartners.map(p => p.id),
          channel: this.broadcastChannel,
          subject: this.broadcastSubject,
          message: this.broadcastMessage,
        };
        const response = await axios.post('/api/v1/manager/partners/broadcast/', payload);
        const successCount = (response.data.results || []).filter(r => !r.error).length;
        this.showSnackbar(`Broadcast sent to ${successCount} partner(s)`, 'success');
        this.selectedPartners = [];
        this.closeBroadcastDialog();
      } catch (error) {
        this.showSnackbar('Error sending broadcast', 'red darken-4');
      } finally {
        this.sendingBroadcast = false;
      }
    },

    // ── Templates avec catégorie "partner" ──
    async fetchTemplates() {
      this.templatesLoading = true;
      try {
        const response = await axios.get('/api/v1/manager/message-templates/?category=partner');
        const data = response.data;
        this.templates = Array.isArray(data) ? data : (data.results || data.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
        this.templates = [];
      } finally {
        this.templatesLoading = false;
      }
    },

    openTemplatePickerDialog(source) {
      this.templatePickerSource = source;
      this.templatePickerSearch = '';
      this.showTemplatePickerDialog = true;
    },
    closeTemplatePickerDialog() {
      this.showTemplatePickerDialog = false;
      this.templatePickerSearch = '';
    },

    selectTemplateFromPicker(tpl) {
      if (this.templatePickerSource === 'individual') {
        if (tpl.channel && this.channels.some(c => c.value === tpl.channel)) {
          this.selectedChannel = tpl.channel;
        }
        this.messageText = tpl.message;
        if (tpl.subject) this.emailSubject = tpl.subject;
        this.$nextTick(() => this.updatePreview());
      } else {
        if (tpl.channel && this.broadcastChannels.some(c => c.value === tpl.channel)) {
          this.broadcastChannel = tpl.channel;
        }
        this.broadcastMessage = tpl.message;
        if (tpl.subject) this.broadcastSubject = tpl.subject;
        this.$nextTick(() => this.updateBroadcastPreview());
      }
      this.closeTemplatePickerDialog();
    },

    openSaveTemplate(source) {
      this.saveTemplateSource = source;
      if (source === 'individual') {
        this.saveTemplateMessage = this.messageText;
        this.saveTemplateSubject = this.selectedChannel === 'email' ? this.emailSubject : '';
      } else {
        this.saveTemplateMessage = this.broadcastMessage;
        this.saveTemplateSubject = this.broadcastChannel === 'email' ? this.broadcastSubject : '';
      }
      this.templateName = '';
      this.showSaveTemplateDialog = true;
      this.$nextTick(() => this.$refs.tplNameInput?.focus());
    },
    closeSaveTemplate() {
      this.showSaveTemplateDialog = false;
      this.templateName = '';
    },

    async confirmSaveTemplate() {
      if (!this.templateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.templateName.trim(),
          subject: this.saveTemplateSubject.trim(),
          message: this.saveTemplateMessage,
          category: 'partner',
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        const newTpl = response.data;
        this.templates.unshift(newTpl);
        this.showSnackbar('Template saved!', 'success');
        this.closeSaveTemplate();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error saving template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    // ── Gestion de la suppression via mini dialogue ──
    requestDeleteTemplate(tpl) {
      this.templateToDelete = tpl;
      this.showDeleteConfirmation = true;
    },
    cancelDeleteTemplate() {
      this.showDeleteConfirmation = false;
      this.templateToDelete = null;
    },
    async confirmDeleteTemplate() {
      if (!this.templateToDelete) return;
      const id = this.templateToDelete.id;
      try {
        await axios.delete(`/api/v1/manager/message-templates/${id}/`);
        this.templates = this.templates.filter(t => t.id !== id);
        this.showSnackbar('Template deleted', 'success');
      } catch (error) {
        this.showSnackbar('Error deleting template', 'red darken-4');
      } finally {
        this.cancelDeleteTemplate();
      }
    },

    openTemplateManager() { this.showTemplateManager = true; },
    closeTemplateManager() { this.showTemplateManager = false; },

    openCreateTemplateDialog() {
      this.createTemplateName = '';
      this.createTemplateSubject = 'Message from PettyCash';
      this.createTemplateMessage = '';
      this.showCreateTemplateDialog = true;
      this.$nextTick(() => {
        if (this.$refs.createTplNameInput) this.$refs.createTplNameInput.focus();
      });
    },
    closeCreateTemplateDialog() {
      this.showCreateTemplateDialog = false;
    },
    async confirmCreateTemplate() {
      if (!this.createTemplateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.createTemplateName.trim(),
          subject: this.createTemplateSubject.trim(),
          message: this.createTemplateMessage,
          category: 'partner',
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        const newTpl = response.data;
        this.templates.unshift(newTpl);
        this.showSnackbar('Template created!', 'success');
        this.closeCreateTemplateDialog();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error creating template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    showSnackbar(message, color) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', color);
      this.$store.commit('activateSnackBar', true);
    },
  },
};
</script>





<style scoped>
/* ═══════════════════════════════════════════════════════
   CSS CUSTOM PROPERTIES
   ═══════════════════════════════════════════════════════ */
.dash-root {
  --clr-primary: #5c6bc0;
  --clr-primary-light: #eef0fc;
  --clr-green: #2e7d32;
  --clr-purple: #6a1b9a;
  --clr-purple-light: #f3e5f5;
  --clr-wa: #25D366;
  --clr-email: #ea4335;
  --clr-push: #f4a84a;
  --clr-text: #2d3142;
  --clr-muted: #9aa0ba;
  --clr-border: #e9ecf5;
  --clr-bg: #f4f6fb;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-pill: 20px;

  padding: 12px;
  background: var(--clr-bg);
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--clr-text);
  box-sizing: border-box;
}

/* ═══════════════════════════════════════════════════════
   HEADER
   ═══════════════════════════════════════════════════════ */
.dash-header {
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: 0 1px 4px rgba(44,51,73,.07);
  margin-bottom: 12px;
  overflow: hidden;
}

.dash-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--clr-border);
  gap: 10px;
  flex-wrap: wrap;
}

.dash-header__brand { display: flex; align-items: center; gap: 10px; min-width: 0; }

.dash-header__icon {
  width: 38px; height: 38px; flex-shrink: 0;
  background: var(--clr-primary-light); color: var(--clr-primary);
  border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  font-size: 16px;
}

.dash-header__title {
  margin: 0; font-size: 18px; font-weight: 700;
  letter-spacing: -0.3px; color: var(--clr-text); line-height: 1.2;
}
.dash-header__subtitle { margin: 2px 0 0; font-size: 11px; color: var(--clr-muted); font-weight: 500; }

.dash-header__actions {
  display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
}

/* Selection pill */
.selection-pill {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; color: var(--clr-primary); font-weight: 600;
  background: var(--clr-primary-light); padding: 5px 12px; border-radius: var(--radius-pill);
  border: 1px solid #dde1f8; white-space: nowrap;
}

/* Buttons */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: var(--radius-pill);
  font-size: 13px; font-weight: 600;
  border: none; cursor: pointer; transition: all .18s ease;
  white-space: nowrap; outline: none; touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.btn--templates-manager {
  background: var(--clr-purple-light); color: var(--clr-purple);
  border: 1px solid #e1bee7;
}
.btn--templates-manager:hover { background: #e1bee7; }

.btn--broadcast { background: var(--clr-primary-light); color: var(--clr-muted); border: 1px solid transparent; }
.btn--broadcast--active { background: var(--clr-primary); color: #fff; box-shadow: 0 2px 8px rgba(92,107,192,.3); }
.btn--broadcast--active:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.btn--broadcast:disabled { opacity: .5; cursor: not-allowed; }

.btn--refresh { background: transparent; color: #6b7a99; border: 1px solid var(--clr-border); padding: 8px 12px; }
.btn--refresh:hover:not(:disabled) { background: var(--clr-bg); border-color: #dde1f8; color: var(--clr-primary); }

.btn--ghost { background: transparent; border: 1px solid var(--clr-border); color: #6b7a99; }
.btn--ghost:hover { background: var(--clr-bg); color: var(--clr-primary); }

.btn--send { background: var(--clr-primary); color: #fff; box-shadow: 0 2px 8px rgba(92,107,192,.3); }
.btn--send:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.btn--send--whatsapp { background: var(--clr-wa); box-shadow: 0 2px 8px rgba(37,211,102,.3); }
.btn--send--email    { background: var(--clr-email); box-shadow: 0 2px 8px rgba(234,67,53,.25); }
.btn--send--webpush  { background: var(--clr-push); box-shadow: 0 2px 8px rgba(244,168,74,.3); }
.btn--send--broadcast { background: var(--clr-primary); }
.btn--send:disabled { opacity: .5; cursor: not-allowed; transform: none !important; }

.btn--save-confirm {
  background: var(--clr-purple); color: #fff;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: var(--radius-pill); font-size: 13px; font-weight: 600;
  border: none; cursor: pointer; transition: all .18s ease;
  box-shadow: 0 2px 8px rgba(106,27,154,.25);
}
.btn--save-confirm:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
.btn--save-confirm:disabled { opacity: .5; cursor: not-allowed; }

/* ═══════════════════════════════════════════════════════
   STATS GRID
   ═══════════════════════════════════════════════════════ */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 12px 16px;
}

.stat-card {
  background: #fff; border-radius: var(--radius-md); padding: 12px 14px;
  display: flex; align-items: center; gap: 10px;
  position: relative; overflow: hidden; border: 1px solid #f0f2f8;
  transition: transform .15s ease, box-shadow .15s ease;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(44,51,73,.06); }

.stat-card__icon {
  width: 36px; height: 36px; border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; flex-shrink: 0;
}
.stat-card__body { flex: 1; min-width: 0; }
.stat-card__val { display: block; font-size: 20px; font-weight: 700; color: var(--clr-text); line-height: 1.1; }
.stat-card__lbl { display: block; font-size: 11px; color: var(--clr-muted); font-weight: 500; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stat-card__bar { position: absolute; bottom: 0; left: 0; height: 3px; border-radius: 0; transition: width .6s ease; }

/* ═══════════════════════════════════════════════════════
   TABLE WRAP
   ═══════════════════════════════════════════════════════ */
.table-wrap {
  background: #fff; border-radius: var(--radius-lg);
  box-shadow: 0 1px 4px rgba(44,51,73,.07); overflow: hidden;
}

.table-toolbar {
  padding: 12px 14px; border-bottom: 1px solid var(--clr-border);
  display: flex; flex-direction: column; gap: 10px;
}

.toolbar-top-row {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}

.filter-group { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 140px; }
.filter-group__icon { color: var(--clr-muted); font-size: 13px; flex-shrink: 0; }
.filter-select { max-width: 200px; flex: 1; }

.search-group {
  display: flex; align-items: center; gap: 8px;
  background: #f9fafc; border: 1.5px solid var(--clr-border); border-radius: var(--radius-sm);
  padding: 0 12px; transition: border-color .2s ease, background .2s ease;
  width: 100%;
}
.search-group:focus-within { border-color: var(--clr-primary); background: #fff; }
.search-group__icon { color: var(--clr-muted); font-size: 13px; flex-shrink: 0; }
.search-input {
  flex: 1; border: none; outline: none; padding: 10px 0;
  background: transparent; font-size: 14px; color: var(--clr-text); min-width: 0;
}
.search-input::placeholder { color: #b0b6cc; }
.search-clear {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border: none; background: transparent;
  color: var(--clr-muted); cursor: pointer; font-size: 11px;
  border-radius: 50%; transition: all .15s ease; flex-shrink: 0;
}
.search-clear:hover { background: var(--clr-primary-light); color: var(--clr-primary); }

.table-meta {
  font-size: 13px; color: #6b7a99; font-weight: 500;
  display: flex; align-items: center; gap: 6px; white-space: nowrap; flex-shrink: 0;
}
.table-meta i { color: var(--clr-primary); }

/* Desktop table */
.mobile-cards  { display: none; }
.desktop-table { display: block; }

.partners-table ::v-deep .v-data-table-header { background: #f9fafc !important; }
.partners-table ::v-deep th { font-size: 11px !important; font-weight: 700 !important; color: #6b7a99 !important; text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid var(--clr-border) !important; padding: 10px 14px !important; }
.partners-table ::v-deep td { font-size: 13px !important; color: var(--clr-text) !important; border-bottom: 1px solid #f0f2f8 !important; padding: 11px 14px !important; }
.partners-table ::v-deep tr:hover { background: #f8f9fc !important; }
.partners-table ::v-deep .v-data-footer { border-top: 1px solid var(--clr-border) !important; font-size: 12px; color: #6b7a99; }

/* ═══════════════════════════════════════════════════════
   MOBILE PARTNER CARDS
   ═══════════════════════════════════════════════════════ */
.mobile-loading {
  padding: 32px; text-align: center; color: var(--clr-muted); font-size: 14px;
}
.partner-card {
  border-bottom: 1px solid #f0f2f8; padding: 14px;
  transition: background .12s ease;
}
.partner-card:last-of-type { border-bottom: none; }
.partner-card--selected { background: #f6f7fd; }

.partner-card__top {
  display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px;
}
.partner-card__check {
  flex-shrink: 0; margin-top: 2px;
  display: flex; align-items: center;
}
.partner-card__check input { width: 18px; height: 18px; cursor: pointer; accent-color: var(--clr-primary); }

.partner-card__info { flex: 1; min-width: 0; }
.partner-card__name {
  font-size: 14px; font-weight: 700; color: var(--clr-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.partner-card__email {
  font-size: 12px; color: var(--clr-muted); margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.partner-card__meta {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-bottom: 10px; padding-left: 28px;
}

.partner-card__actions {
  display: flex; align-items: center; gap: 6px; padding-left: 28px;
}

.mobile-count {
  text-align: center; font-size: 12px; color: var(--clr-muted);
  padding: 12px; border-top: 1px solid var(--clr-border);
}

/* ═══════════════════════════════════════════════════════
   BADGES & TABLE CELLS
   ═══════════════════════════════════════════════════════ */
.type-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: var(--radius-pill);
  font-size: 11px; font-weight: 600; border: 1px solid transparent;
  white-space: nowrap; flex-shrink: 0;
}
.type-badge--marketer { background: #e8f5e9; color: var(--clr-green); border-color: #c8e6c9; }
.type-badge--investor { background: var(--clr-purple-light); color: var(--clr-purple); border-color: #e1bee7; }

.phone-cell { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #4a4f6a; }
.phone-cell .fab.fa-whatsapp { color: var(--clr-wa); font-size: 13px; }
.phone-cell--missing { color: #d99a3f; font-size: 11px; }

.affiliate-count {
  display: inline-flex; align-items: center; gap: 5px;
  background: var(--clr-primary-light); color: var(--clr-primary);
  padding: 3px 10px; border-radius: var(--radius-pill);
  font-size: 12px; font-weight: 600;
}
.date-cell { font-size: 12px; color: #6b7a99; font-weight: 500; }

.action-group { display: flex; gap: 4px; justify-content: center; }
.action-btn {
  min-width: 32px; height: 32px; border-radius: var(--radius-sm);
  border: none; display: inline-flex; align-items: center; justify-content: center;
  gap: 5px; cursor: pointer; font-size: 12px; transition: all .15s ease;
  background: transparent; color: var(--clr-muted);
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;
  padding: 0 8px;
}
.action-btn:hover { transform: translateY(-1px); }
.action-btn--view:hover     { background: var(--clr-primary-light); color: var(--clr-primary); }
.action-btn--email:hover    { background: #fce4ec; color: var(--clr-email); }
.action-btn--whatsapp:hover { background: #e9fbf0; color: var(--clr-wa); }
.action-btn--push:hover     { background: #fff3e0; color: var(--clr-push); }

.empty-table { text-align: center; padding: 48px 20px; color: var(--clr-muted); }
.empty-table i { font-size: 36px; margin-bottom: 12px; opacity: .4; display: block; }
.empty-table p { font-size: 15px; font-weight: 600; color: #6b7a99; margin: 0 0 4px; }
.empty-table small { font-size: 12px; }

/* ═══════════════════════════════════════════════════════
   MODAL OVERLAY & CARD
   ═══════════════════════════════════════════════════════ */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(30,34,58,.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex; align-items: flex-end; justify-content: center;
  padding: 0;
}

.modal-card {
  background: #fff;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  width: 100%; max-width: 100%;
  max-height: 92vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 -4px 24px rgba(44,51,73,.15);
}

.modal-card--save-tpl { max-width: 100%; }
.modal-card--manager  { max-width: 100%; }

.modal-card__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid var(--clr-border); gap: 10px;
  flex-shrink: 0;
}
.modal-card__brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
.modal-card__icon {
  width: 40px; height: 40px; border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; flex-shrink: 0;
  background: var(--clr-primary-light); color: var(--clr-primary);
}
.modal-card__icon--email    { background: #fce4ec; color: var(--clr-email); }
.modal-card__icon--whatsapp { background: #e9fbf0; color: var(--clr-wa); }
.modal-card__icon--webpush  { background: #fff3e0; color: var(--clr-push); }
.modal-card__icon--broadcast { background: var(--clr-primary-light); color: var(--clr-primary); }
.modal-card__icon--tpl      { background: var(--clr-purple-light); color: var(--clr-purple); }

.modal-card__title    { font-size: 15px; font-weight: 700; color: var(--clr-text); line-height: 1.2; }
.modal-card__subtitle { font-size: 12px; color: var(--clr-muted); margin-top: 2px; }

.modal-close {
  width: 34px; height: 34px; border: none; background: transparent;
  color: var(--clr-muted); border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 14px; transition: all .15s ease; flex-shrink: 0;
}
.modal-close:hover { background: var(--clr-bg); color: var(--clr-primary); }

.modal-card__body {
  flex: 1; min-height: 0; overflow-y: auto;
  padding: 16px; display: flex; flex-direction: column; gap: 14px;
  -webkit-overflow-scrolling: touch;
}
.modal-card__body::-webkit-scrollbar { width: 4px; }
.modal-card__body::-webkit-scrollbar-thumb { background: var(--clr-border); border-radius: 4px; }

.modal-card__foot {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 16px; border-top: 1px solid var(--clr-border);
  background: #f9fafc; flex-shrink: 0;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}

/* ═══════════════════════════════════════════════════════
   SPLIT LAYOUT (Form + Preview)
   ═══════════════════════════════════════════════════════ */
.modal-body--split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.modal-form-col { min-width: 0; }
.modal-preview-col { min-width: 0; }

.preview-section--sticky {
  position: sticky;
  top: 0;
}

/* ═══════════════════════════════════════════════════════
   USE TEMPLATE BUTTON
   ═══════════════════════════════════════════════════════ */
.btn-use-template {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 12px; border-radius: var(--radius-sm);
  background: var(--clr-purple-light); color: var(--clr-purple);
  border: 1px solid #e1bee7; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s ease;
}
.btn-use-template:hover { background: #e1bee7; }
.btn-use-template .tpl-badge {
  margin-left: auto;
  background: var(--clr-purple); color: #fff;
  padding: 2px 8px; border-radius: 10px; font-size: 11px;
  font-weight: 700;
}

/* ═══════════════════════════════════════════════════════
   TEMPLATE PICKER DIALOG
   ═══════════════════════════════════════════════════════ */
.modal-card--picker { max-width: 100%; }

.tpl-search--dialog {
  display: flex; align-items: center; gap: 8px;
  background: #f9fafc; border: 1.5px solid var(--clr-border);
  border-radius: var(--radius-sm); padding: 0 12px;
  margin-bottom: 14px;
}
.tpl-search--dialog i { color: var(--clr-muted); font-size: 13px; }
.tpl-search--dialog input {
  flex: 1; border: none; outline: none; padding: 10px 0;
  background: transparent; font-size: 14px; color: var(--clr-text);
}
.tpl-search--dialog input::placeholder { color: #b0b6cc; }

.tpl-picker-list {
  display: flex; flex-direction: column; gap: 10px;
  max-height: 55vh; overflow-y: auto; -webkit-overflow-scrolling: touch;
}

.tpl-picker-item {
  border: 1.5px solid var(--clr-border); border-radius: var(--radius-md);
  padding: 12px 14px; cursor: pointer; transition: all .15s ease;
  background: #fff;
}
.tpl-picker-item:hover { border-color: var(--clr-purple); box-shadow: 0 2px 10px rgba(106,27,154,.08); }

.tpl-picker-item__top {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin-bottom: 6px;
}
.tpl-picker-item__subject {
  font-size: 12px; color: #6b7a99; margin: 0 0 4px;
  display: flex; align-items: center; gap: 5px;
}
.tpl-picker-item__body {
  font-size: 13px; line-height: 1.5; color: var(--clr-text);
  margin: 0 0 10px; white-space: pre-wrap; word-break: break-word;
}
.tpl-picker-item__footer {
  display: flex; align-items: center; justify-content: space-between;
}

/* ═══════════════════════════════════════════════════════
   TEMPLATE MANAGER (existing)
   ═══════════════════════════════════════════════════════ */
.tpl-manager-item__date { font-size: 11px; color: var(--clr-muted); display: flex; align-items: center; gap: 5px; }
.tpl-all-channels-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px; border-radius: 10px; font-size: 10px; font-weight: 700;
  background: var(--clr-purple-light); color: var(--clr-purple); border: 1px solid #e1bee7;
  white-space: nowrap;
}
.tpl-compat-icons { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #b0b6cc; }
.tpl-compat-icons .fab.fa-whatsapp { color: #b2dfdb; }
.tpl-compat-icons .fas.fa-envelope  { color: #f8bbd0; }
.tpl-compat-icons .fas.fa-bell      { color: #ffe0b2; }
.tpl-del-btn {
  min-width: 32px; height: 32px; border-radius: var(--radius-sm); border: none;
  background: transparent; color: #d32f2f; cursor: pointer; font-size: 13px;
  display: flex; align-items: center; justify-content: center; transition: all .15s ease;
}
.tpl-del-btn:hover { background: #fce4ec; }

.tpl-manager-list { display: flex; flex-direction: column; gap: 10px; }
.tpl-manager-item {
  border: 1.5px solid var(--clr-border); border-radius: var(--radius-md);
  padding: 12px 14px; background: #fff; transition: box-shadow .15s ease;
}
.tpl-manager-item:hover { box-shadow: 0 2px 10px rgba(44,51,73,.07); }
.tpl-manager-item__top {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 8px; margin-bottom: 6px; flex-wrap: wrap;
}
.tpl-manager-item__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.tpl-manager-item__subject { font-size: 12px; color: #6b7a99; font-weight: 500; margin: 0 0 4px; display: flex; align-items: center; gap: 5px; }
.tpl-manager-item__body { font-size: 13px; color: var(--clr-text); line-height: 1.5; margin: 0 0 10px; white-space: pre-wrap; word-break: break-word; }
.tpl-manager-item__footer { display: flex; align-items: center; justify-content: space-between; }

/* ═══════════════════════════════════════════════════════
   RECIPIENT BAR
   ═══════════════════════════════════════════════════════ */
.recipient-bar {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; background: var(--clr-bg);
  border-radius: var(--radius-sm); border: 1px solid var(--clr-border);
}
.recipient-bar__label {
  font-size: 11px; font-weight: 700; color: var(--clr-muted);
  text-transform: uppercase; letter-spacing: .5px; flex-shrink: 0; padding-top: 2px;
}
.recipient-chips { display: flex; flex-wrap: wrap; gap: 5px; flex: 1; min-width: 0; }
.recipient-chip {
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: var(--radius-pill); font-size: 12px; font-weight: 600;
  color: var(--clr-primary); background: var(--clr-primary-light);
  border: 1px solid #dde1f8; white-space: nowrap; max-width: 120px;
  overflow: hidden; text-overflow: ellipsis;
}
.recipient-chip--more { background: var(--clr-bg); color: var(--clr-muted); border-color: var(--clr-border); }

/* ═══════════════════════════════════════════════════════
   CHANNEL PICKER
   ═══════════════════════════════════════════════════════ */
.channel-picker { display: flex; gap: 8px; flex-wrap: wrap; }
.channel-chip {
  flex: 1; min-width: 80px;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 12px; border-radius: var(--radius-pill); font-size: 13px; font-weight: 600;
  border: 1.5px solid var(--clr-border); background: #f9fafc; color: #6b7a99;
  cursor: pointer; transition: all .15s ease; outline: none;
  touch-action: manipulation;
}
.channel-chip:hover { border-color: #dde1f8; background: var(--clr-bg); color: var(--clr-primary); }
.channel-chip--active { background: var(--clr-primary-light); border-color: var(--clr-primary); color: var(--clr-primary); box-shadow: 0 2px 6px rgba(92,107,192,.12); }

/* ═══════════════════════════════════════════════════════
   FORM ELEMENTS
   ═══════════════════════════════════════════════════════ */
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-label {
  font-size: 12px; font-weight: 700; color: var(--clr-text);
  display: flex; justify-content: space-between; align-items: flex-start; gap: 6px;
  flex-wrap: wrap;
}
.form-hint { font-size: 11px; color: var(--clr-muted); font-weight: 500; line-height: 1.4; }
.form-input, .form-textarea {
  border: 1.5px solid var(--clr-border); border-radius: var(--radius-sm);
  padding: 10px 12px; font-family: inherit; font-size: 14px; color: var(--clr-text);
  background: #f9fafc; transition: border-color .2s ease, background .2s ease;
  outline: none; width: 100%; box-sizing: border-box;
  -webkit-appearance: none;
}
.form-input:focus, .form-textarea:focus { border-color: var(--clr-primary); background: #fff; }
.form-textarea { resize: vertical; min-height: 90px; line-height: 1.6; }
.form-input::placeholder, .form-textarea::placeholder { color: #b0b6cc; }

.form-actions-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.char-count { font-size: 11px; color: var(--clr-muted); font-weight: 500; }
.char-count--warn { color: #e57373; font-weight: 700; }
.btn-save-tpl {
  display: inline-flex; align-items: center; gap: 5px;
  background: var(--clr-purple-light); color: var(--clr-purple);
  border: 1px solid #e1bee7; border-radius: 16px;
  padding: 5px 12px; font-size: 11px; font-weight: 600; cursor: pointer;
  transition: all .15s ease; white-space: nowrap; touch-action: manipulation;
}
.btn-save-tpl:hover { background: #e1bee7; }

.tpl-msg-preview {
  background: #f9fafc; border: 1.5px solid var(--clr-border); border-radius: var(--radius-sm);
  padding: 10px 12px; font-size: 12px; color: #4a4f6a; line-height: 1.6;
  white-space: pre-wrap; word-break: break-word; max-height: 100px; overflow-y: auto;
}

.tpl-info-note {
  display: flex; align-items: flex-start; gap: 8px;
  background: var(--clr-purple-light); color: var(--clr-purple);
  border: 1px solid #e1bee7; border-radius: var(--radius-sm);
  padding: 10px 12px; font-size: 12px; font-weight: 500; line-height: 1.5;
}

/* ═══════════════════════════════════════════════════════
   PREVIEW SECTION
   ═══════════════════════════════════════════════════════ */
.preview-section { border-top: 1px solid var(--clr-border); padding-top: 14px; display: flex; flex-direction: column; gap: 10px; }
.preview-header {
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; color: #6b7a99;
  text-transform: uppercase; letter-spacing: .5px;
}
.preview-header i { color: var(--clr-primary); }
.preview-channel {
  margin-left: auto; font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: 10px;
  background: var(--clr-primary-light); color: var(--clr-primary);
  text-transform: none; letter-spacing: 0;
}

/* WhatsApp */
.preview-whatsapp { display: flex; justify-content: center; }
.wa-phone {
  width: 100%; max-width: 300px;
  background: #e5ddd5; border-radius: var(--radius-md); overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,.1); border: 1px solid #d1d1d1;
}
.wa-phone__header {
  background: #075e54; color: #fff; padding: 10px 14px;
  display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600;
}
.wa-phone__chat {
  padding: 14px 10px; min-height: 90px;
  display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
}
.wa-bubble {
  background: #dcf8c6; border-radius: 10px 0 10px 10px; padding: 8px 12px;
  max-width: 90%; box-shadow: 0 1px 2px rgba(0,0,0,.08);
}
.wa-bubble__text { font-size: 13px; line-height: 1.5; color: #1f2937; white-space: pre-wrap; word-break: break-word; }
.wa-bubble__meta { display: flex; justify-content: flex-end; align-items: center; gap: 4px; margin-top: 4px; }
.wa-bubble__time { font-size: 10px; color: #888; }
.wa-bubble__tick { font-size: 10px; color: #4fc3f7; letter-spacing: -1px; }

/* Email */
.preview-email { display: flex; justify-content: center; }
.email-frame {
  width: 100%; background: #fff; border-radius: var(--radius-sm);
  overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,.06);
  border: 1px solid var(--clr-border);
}
.email-frame__bar {
  background: var(--clr-bg); padding: 8px 12px;
  display: flex; align-items: center; gap: 8px;
  border-bottom: 1px solid var(--clr-border);
}
.email-frame__dots { display: flex; gap: 4px; }
.email-frame__dots span { width: 8px; height: 8px; border-radius: 50%; background: #d1d5db; }
.email-frame__dots span:nth-child(1) { background: #ef4444; }
.email-frame__dots span:nth-child(2) { background: #f59e0b; }
.email-frame__dots span:nth-child(3) { background: #10b981; }
.email-frame__url {
  flex: 1; text-align: center; font-size: 11px; color: var(--clr-muted);
  background: #fff; padding: 3px 8px; border-radius: 4px; border: 1px solid var(--clr-border);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.email-frame__body { padding: 14px; }
.email-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #f0f2f8;
  gap: 8px; flex-wrap: wrap;
}
.email-header__from { display: flex; align-items: center; gap: 8px; min-width: 0; }
.email-avatar {
  width: 30px; height: 30px; flex-shrink: 0; border-radius: 50%;
  background: var(--clr-primary); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700;
}
.email-header__name { font-size: 13px; font-weight: 700; color: var(--clr-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.email-header__addr { font-size: 10px; color: var(--clr-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.email-header__date { font-size: 10px; color: var(--clr-muted); flex-shrink: 0; }
.email-subject { font-size: 13px; font-weight: 700; color: var(--clr-text); margin-bottom: 8px; }
.email-body { font-size: 12px; line-height: 1.6; color: #4a4f6a; white-space: pre-wrap; word-break: break-word; }

/* WebPush */
.preview-webpush { display: flex; justify-content: center; }
.push-frame {
  width: 100%; max-width: 320px;
  background: #fff; border-radius: var(--radius-md); padding: 14px;
  box-shadow: 0 4px 16px rgba(0,0,0,.08); border: 1px solid var(--clr-border);
  display: flex; flex-direction: column; gap: 8px;
}
.push-frame__header { display: flex; justify-content: space-between; align-items: center; }
.push-app { display: flex; align-items: center; gap: 8px; }
.push-app__icon {
  width: 26px; height: 26px; border-radius: var(--radius-sm);
  background: var(--clr-primary); color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: 11px;
}
.push-app__name { font-size: 12px; font-weight: 700; color: var(--clr-text); }
.push-time { font-size: 11px; color: var(--clr-muted); font-weight: 500; }
.push-title { font-size: 13px; font-weight: 700; color: var(--clr-text); }
.push-body { font-size: 12px; line-height: 1.5; color: #6b7a99; white-space: pre-wrap; word-break: break-word; }

/* ═══════════════════════════════════════════════════════
   TRANSITIONS
   ═══════════════════════════════════════════════════════ */
.modal-enter-active, .modal-leave-active { transition: opacity .25s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter-active .modal-card, .modal-leave-active .modal-card { transition: transform .25s cubic-bezier(.16,1,.3,1), opacity .25s ease; }
.modal-enter .modal-card, .modal-leave-to .modal-card { transform: translateY(40px); opacity: 0; }

.pop-enter-active, .pop-leave-active { transition: all .2s ease; }
.pop-enter, .pop-leave-to { opacity: 0; transform: scale(.9); }

.field-slide-enter-active, .field-slide-leave-active { transition: all .22s ease; max-height: 200px; opacity: 1; overflow: hidden; }
.field-slide-enter, .field-slide-leave-to { max-height: 0; opacity: 0; }

/* ═══════════════════════════════════════════════════════
   RESPONSIVE — TABLET (≥ 640px)
   ═══════════════════════════════════════════════════════ */
@media (min-width: 640px) {
  .dash-root { padding: 16px; }
  .dash-header__inner { padding: 16px 20px; }

  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
    padding: 14px 20px;
  }

  .table-toolbar { padding: 12px 18px; }
  .toolbar-top-row { flex-wrap: nowrap; }

  .modal-overlay {
    align-items: center;
    padding: 16px;
  }
  .modal-card {
    border-radius: var(--radius-lg);
    max-width: 580px;
    max-height: 90vh;
  }
  .modal-card--broadcast { max-width: 620px; }
  .modal-card--save-tpl  { max-width: 460px; }
  .modal-card--manager   { max-width: 640px; }
  .modal-card--picker    { max-width: 680px; }

  .modal-enter .modal-card, .modal-leave-to .modal-card {
    transform: scale(.96) translateY(8px);
  }

  /* Show desktop table, hide mobile cards */
  .desktop-table { display: block; }
  .mobile-cards  { display: none; }
}

/* ═══════════════════════════════════════════════════════
   RESPONSIVE — MOBILE (< 640px)
   ═══════════════════════════════════════════════════════ */
@media (max-width: 639px) {
  /* Hide desktop table, show cards */
  .desktop-table { display: none !important; }
  .mobile-cards  { display: block; }

  .dash-header__inner { padding: 12px 14px; }
  .dash-header__title { font-size: 16px; }

  /* Stack brand + actions */
  .dash-header__inner { flex-direction: column; align-items: stretch; }
  .dash-header__actions { justify-content: flex-start; flex-wrap: nowrap; overflow-x: auto; padding-bottom: 2px; }
  .dash-header__actions::-webkit-scrollbar { display: none; }

  /* Hide button labels on very small screens */
  .btn-label { display: none; }
  .btn { padding: 9px 12px; }
  .btn--templates-manager, .btn--broadcast { padding: 9px 14px; }

  .selection-pill .pill-text { display: none; }
  .selection-pill { padding: 7px 10px; }

  /* 2-col stats on mobile */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 10px 12px; gap: 8px;
  }
  .stat-card:first-child { grid-column: span 1; }

  .stat-card { padding: 10px 12px; gap: 8px; }
  .stat-card__val { font-size: 18px; }
  .stat-card__icon { width: 32px; height: 32px; font-size: 13px; }

  .table-toolbar { padding: 10px 12px; }
  .toolbar-top-row { gap: 8px; }

  /* Modals: full-width bottom sheet */
  .modal-overlay { padding: 0; align-items: flex-end; }
  .modal-card {
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    max-width: 100%; max-height: 94vh;
  }
  .modal-card__head { padding: 12px 14px; }
  .modal-card__body { padding: 14px; gap: 12px; }
  .modal-card__foot { padding: 10px 14px; padding-bottom: max(10px, env(safe-area-inset-bottom)); }

  /* Split layout becomes stacked */
  .modal-body--split {
    grid-template-columns: 1fr;
  }

  /* Bigger tap targets on channel chips */
  .channel-chip { padding: 10px 12px; font-size: 13px; }

  /* Full-width footer buttons */
  .modal-card__foot { flex-direction: row; }
  .modal-card__foot .btn--ghost { flex: 1; justify-content: center; }
  .modal-card__foot .btn--send,
  .modal-card__foot .btn--save-confirm,
  .modal-card__foot .btn--broadcast { flex: 2; justify-content: center; }
}

/* ═══════════════════════════════════════════════════════
   RESPONSIVE — DESKTOP (≥ 900px)
   ═══════════════════════════════════════════════════════ */
@media (min-width: 900px) {
  .dash-root { padding: 20px; }
  .dash-header__inner { padding: 18px 24px; }
  .stats-grid { gap: 12px; padding: 16px 24px; }
  .table-toolbar { padding: 14px 20px; flex-direction: row; align-items: center; gap: 10px; }
  .toolbar-top-row { flex: 1; }
  .search-group { width: auto; flex: 1; max-width: 300px; }

  /* Restore button labels */
  .btn-label { display: inline; }
  .btn { padding: 8px 16px; }
  .selection-pill .pill-text { display: inline; }
  .selection-pill { padding: 6px 14px; }
}

/* ═══════════════════════════════════════════════════════
   NOUVEAUX STYLES POUR SELECT ALL ET CREATE TEMPLATE
   ═══════════════════════════════════════════════════════ */
.btn--select-all {
  background: transparent;
  border: 1.5px solid var(--clr-border);
  color: #6b7a99;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}
.btn--select-all:hover {
  border-color: var(--clr-primary);
  color: var(--clr-primary);
  background: var(--clr-primary-light);
}

/* Manager header create button */
.btn--create-template-manager {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: var(--radius-pill);
  background: var(--clr-purple-light);
  color: var(--clr-purple);
  border: 1px solid #e1bee7;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s ease;
  margin-right: 8px;
}
.btn--create-template-manager:hover {
  background: #e1bee7;
}

.modal-card__head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 639px) {
  .btn--select-all .btn-label {
    display: none;
  }
}

.v-snack__wrapper,
.v-snackbar__wrapper,
.snackbar,
.v-snackbar__content {
  z-index: 10000 !important;
}
</style>
